import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { api } from "@/lib/api";
import type { FilterGroup } from "@/lib/filters";
import {
  type DateFilter,
  type FilterValue,
  type NumberFilter,
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";
import type { CampaignMetrics } from "./use-campaign-metrics";

export const CAMPAIGNS_QUERY_KEY = ["datahub", "campaigns"] as const;

export interface Campaign {
  id: string;
  name: string;
  source_record_id: string;
  source_record_type: string;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
  type: string | null;
  additional_properties: {
    content_id: string | null;
  };
  metrics?: Record<keyof CampaignMetrics, number | string | undefined>;
  formulas?: Record<keyof CampaignMetrics, number | string | undefined>;
  campaign_ancestry_list?: string[] | null;
}

// CampaignWithMetrics includes all Campaign fields plus unnested metrics as top-level properties
export interface CampaignWithMetrics
  extends Omit<Campaign, "metrics" | "formulas"> {
  // Allow any metric key from CampaignMetrics as a property
  [key: string]: string | number | null | undefined | object;
}

export interface CampaignsResponse {
  results: Campaign[];
  total: number;
  limit: number;
  offset: number;
  client_warning?: string;
}

// Re-export the filter types for convenience
export type { DateFilter, FilterValue, NumberFilter };

// Extend the shared QueryParams interface for campaign-specific parameters
export interface CampaignsQueryParams extends QueryParams {
  metrics?: Array<keyof CampaignMetrics>;
  metric_filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
  };
  formulas?: Array<{
    id: string;
    name: string;
    formulaAST: unknown;
    dataType:
      | "number"
      | "string"
      | "currency"
      | "percentage"
      | "date"
      | "boolean";
    description?: string;
  }>;
}

/**
 * Hook for fetching campaigns by IDs
 */
export function useCampaignsByIds(campaignIds: string[] | undefined) {
  const searchParams = {
    campaign_ids: campaignIds?.join(",") ?? "",
  };

  const endpoint = `datahub/campaigns/by-ids/${
    searchParams ? `?${new URLSearchParams(searchParams)}` : ""
  }`;

  return api.useQuery<CampaignsResponse>(endpoint, {
    enabled: !!campaignIds?.length,
    // Cache the result for longer to avoid unnecessary refetches
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

interface UseCampaignSearchOptions {
  search?: string;
  enabled?: boolean;
}

/**
 * Hook for searching campaigns
 */
export function useCampaignSearch(options: UseCampaignSearchOptions = {}) {
  const debouncedSearch = useDebounce(options.search, 300);

  return useQuery<CampaignsResponse, Error>({
    queryKey: ["datahub/campaigns/search/", { search: debouncedSearch }],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (debouncedSearch) {
        searchParams.set("search", debouncedSearch);
      }

      return api.get<CampaignsResponse>(
        `datahub/campaigns/search/?${searchParams}`,
      );
    },
    enabled: options.enabled !== false && !!debouncedSearch,
  });
}

/**
 * Hook for fetching campaigns with pagination, sorting, and search
 */
export function useCampaigns(options?: CampaignsQueryParams) {
  const {
    limit = 25,
    sort_by = "name",
    sort_direction = "asc",
    search = "",
    ...filterParams
  } = options || {};

  const debouncedSearch = useDebounce(search, 300);

  return useQuery<CampaignsResponse, Error>({
    queryKey: [
      "datahub/campaigns/",
      { sort_by, sort_direction, search: debouncedSearch, ...filterParams },
    ],
    queryFn: async () => {
      // Create base params
      const params: CampaignsQueryParams = {
        limit,
        offset: 0,
        sort_by,
        sort_direction,
        search: debouncedSearch || undefined,
        ...filterParams,
      };

      // Process all parameters into a URLSearchParams object
      const searchParams = processQueryParams(params);

      return api.get<CampaignsResponse>(`datahub/campaigns/?${searchParams}`);
    },
  });
}

/**
 * Hook for fetching campaigns with infinite scrolling
 */
export function useInfiniteCampaigns(options?: CampaignsQueryParams) {
  const {
    limit = 25,
    sort_by = "name",
    sort_direction = "asc",
    search = "",
    metrics = [],
    formulas = [],
    metric_filters = {},
    ...filterParams // Extract any additional filter parameters
  } = options || {};

  const debouncedSearch = useDebounce(search, 300);

  return useInfiniteQuery<CampaignsResponse, Error>({
    queryKey: [
      "datahub/campaigns/infinite",
      {
        sort_by,
        sort_direction,
        search: debouncedSearch,
        metrics,
        metric_filters,
        formulas,
        ...filterParams,
      },
    ],
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam * limit : 0;

      // Create base params
      const params: CampaignsQueryParams = {
        limit,
        offset,
        sort_by,
        sort_direction,
        search: debouncedSearch || undefined,
        ...filterParams,
      };

      // Add metrics parameters
      if (metrics.length > 0) {
        params.metrics = metrics;
      }

      // Add metric_filters if present
      if (Object.keys(metric_filters).length > 0) {
        params.metric_filters = metric_filters;
      }

      // Add formulas if present
      if (formulas.length > 0) {
        params.formulas = formulas;
      }

      // Process all parameters into a URLSearchParams object
      const searchParams = processQueryParams(params);

      return api.get<CampaignsResponse>(`datahub/campaigns/?${searchParams}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length;
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.results.length,
        0,
      );

      return totalFetched < lastPage.total ? nextPage : undefined;
    },
  });
}

/**
 * Fetch all campaigns matching the given filters for CSV export
 * This bypasses pagination to get the complete dataset
 */
export function useCampaignsExport() {
  const mutation = useMutation<
    CampaignWithMetrics[],
    Error,
    CampaignsQueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allCampaigns: Campaign[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all campaigns
      while (hasMoreData) {
        // Create query params for this page
        const queryParams: CampaignsQueryParams = {
          ...params,
          limit: 500, // Override the limit that is passed in
          offset,
        };

        // Add metrics parameters if they exist (same as infinite scroll version)
        if (params.metrics && params.metrics.length > 0) {
          queryParams.metrics = params.metrics;
        }

        // Add metric_filters if present
        if (
          params.metric_filters &&
          Object.keys(params.metric_filters).length > 0
        ) {
          queryParams.metric_filters = params.metric_filters;
        }

        // Process parameters into a URLSearchParams object
        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of campaigns
        const response = await api.get<CampaignsResponse>(
          `datahub/campaigns/?${searchParams}`,
        );

        // Add the results to our array
        allCampaigns.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allCampaigns.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      // Transform Campaign[] to CampaignWithMetrics[] by unnesting metrics
      return allCampaigns.map((campaign) => {
        const { metrics, formulas, ...baseObject } = campaign;

        // Create a new object with all base properties
        const campaignWithMetrics: CampaignWithMetrics = { ...baseObject };

        // Add each metric as a top-level property if metrics exist
        if (metrics) {
          for (const [key, value] of Object.entries(metrics)) {
            campaignWithMetrics[`metric_${key}`] = value;
          }
        }

        // Add each formula as a top-level property if formulas exist
        if (formulas) {
          for (const [key, value] of Object.entries(formulas)) {
            campaignWithMetrics[`formula_${key}`] = value;
          }
        }

        return campaignWithMetrics;
      });
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportCampaignsAsync: (params?: CampaignsQueryParams) =>
      mutation.mutateAsync(params),
  };
}

/**
 * Fetch all campaigns matching the given filters for formula evaluation
 * Returns raw Campaign[] data (before flattening) for use with formula evaluation
 */
export function useCampaignsRawExport() {
  const mutation = useMutation<
    Campaign[],
    Error,
    CampaignsQueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allCampaigns: Campaign[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all campaigns
      while (hasMoreData) {
        // Create query params for this page
        const queryParams: CampaignsQueryParams = {
          ...params,
          limit: 500, // Override the limit that is passed in
          offset,
        };

        // Add metrics parameters if they exist (same as infinite scroll version)
        if (params.metrics && params.metrics.length > 0) {
          queryParams.metrics = params.metrics;
        }

        // Add metric_filters if present
        if (
          params.metric_filters &&
          Object.keys(params.metric_filters).length > 0
        ) {
          queryParams.metric_filters = params.metric_filters;
        }

        // Process parameters into a URLSearchParams object
        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of campaigns
        const response = await api.get<CampaignsResponse>(
          `datahub/campaigns/?${searchParams}`,
        );

        // Add the results to our array
        allCampaigns.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allCampaigns.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      // Return raw Campaign[] data (no flattening)
      return allCampaigns;
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportRawCampaignsAsync: (params?: CampaignsQueryParams) =>
      mutation.mutateAsync(params),
  };
}
